import { FottServiceRequestStatus, FottServiceType } from "@services/terminal-services/types";
import type {
  BypassServiceDetailsData,
  FottServiceType as FottServiceTypeValue,
  GantryCraneServiceDetailsData,
  NitrogenSupplyServiceDetailsData,
  PiggingServiceDetailsData,
  TerminalServiceDetailsResponse,
  TerminalServiceUsageEntry,
} from "@services/terminal-services/types";

type ServiceDetails = NonNullable<TerminalServiceDetailsResponse["serviceDetails"]>;

const isSameCalendarMonth = (dateValue?: string | null, today = new Date()) => {
  if (!dateValue) {
    return false;
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth();
};

export const hasRecordedTerminalServiceUsage = ({
  serviceType,
  numberOfUsageHours,
  usageHours,
  usageHoursFott,
}: {
  serviceType?: FottServiceTypeValue | string | null;
  numberOfUsageHours?: number | null;
  usageHours?: TerminalServiceUsageEntry[] | number | null;
  usageHoursFott?: TerminalServiceUsageEntry[] | null;
}) => {
  if (serviceType === FottServiceType.NITROGEN_SUPPLY_SERVICE) {
    return Array.isArray(usageHoursFott) && usageHoursFott.length > 0;
  }

  if (Array.isArray(usageHours)) {
    return usageHours.length > 0;
  }

  return typeof numberOfUsageHours === "number" && numberOfUsageHours > 0;
};

export const getTerminalServiceDateForCancellation = (
  serviceType?: FottServiceTypeValue | string | null,
  serviceDetails?: ServiceDetails | null,
) => {
  if (!serviceDetails) {
    return null;
  }

  switch (serviceType) {
    case FottServiceType.NITROGEN_SUPPLY_SERVICE:
      return (serviceDetails as NitrogenSupplyServiceDetailsData).startTime ?? null;
    case FottServiceType.PIGGING_SERVICE:
      return (serviceDetails as PiggingServiceDetailsData).startingFrom ?? null;
    case FottServiceType.BYPASS_SERVICE:
      return (serviceDetails as BypassServiceDetailsData).sourceDateTime ?? null;
    case FottServiceType.GANTRY_CRANE_SERVICE:
    default:
      return (serviceDetails as GantryCraneServiceDetailsData).bookingFrom ?? null;
  }
};

export const canCancelTerminalService = ({
  status,
  serviceDate,
  serviceType,
  numberOfUsageHours,
  usageHours,
  usageHoursFott,
  today,
}: {
  status?: string | null;
  serviceDate?: string | null;
  serviceType?: FottServiceTypeValue | string | null;
  numberOfUsageHours?: number | null;
  usageHours?: TerminalServiceUsageEntry[] | number | null;
  usageHoursFott?: TerminalServiceUsageEntry[] | null;
  today?: Date;
}) =>
  status === FottServiceRequestStatus.APPROVED &&
  !hasRecordedTerminalServiceUsage({
    serviceType,
    numberOfUsageHours,
    usageHours,
    usageHoursFott,
  }) &&
  isSameCalendarMonth(serviceDate, today);
